import { DataTable, PageWrapper, removeCpfMask, STATUSPAGE } from '@repo/ui';
import { TceManagementUseCaseInterface } from 'domain/usecases';
import { TceListingType } from 'domain/value-objects/tce-listing-type';
import { useFormik } from 'formik';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button, Card, CardBody } from 'reactstrap';
import * as Yup from 'yup';

import { useTceManagementColumns } from '../common/columns-tce-management';
import { TceFilters } from '../components/TceFilters';
import { useTceManagementList } from '../hooks/useTceManagementList';
import { useTceSummary } from '../hooks/useTceSummary';

export const TceManagementList = () => {
  // hooks
  const location = useLocation();
  const navigate = useNavigate();

  const {
    tceManagementData,
    listLoadingStatus,
    paginationMetadata,
    requestGetTceManagement,
    handlePageChange,
    handleSortRemote,
    handleSearchRemote,
    handleRowsPerPageChange,
  } = useTceManagementList();

  const {
    requestGetAllGroupsUser,
    requestGetAllCoursesUser,
    requestGetInternshipLocations,
    requestGetAllOrganizationsUser,
    groupsUser,
    coursesUser,
    organizationsUser,
    loadingReportSelectCourse,
    loadingInternshipLocations,
    loadingReportSelectOrganization,
    internshipLocationsSelectOptions,
  } = useTceSummary();

  // variables
  const isFromNavigation = !!location.state;
  const signatureStatus = location.state?.signatureStatus;
  const situationTitle = location.state?.situationTitle || 'TCEs';
  const listingType = location.state?.listingType as TceListingType;
  const initialFilters = location.state?.filters ? location.state.filters : {};

  // states
  const [currentFilters, setCurrentFilters] = useState(initialFilters);

  const validation = useFormik({
    enableReinitialize: isFromNavigation,
    initialValues: {
      group: initialFilters.group || [],
      local: initialFilters.local || [],
      course: initialFilters.course || [],
      entity: initialFilters.entity || [],
      studentCpf: initialFilters.studentCpf || '',
      studentName: initialFilters.studentName || '',
    },
    validationSchema: Yup.object({
      course: Yup.array(),
      entity: Yup.array(),
      group: Yup.array(),
      local: Yup.array(),
      studentName: Yup.string(),
      studentCpf: Yup.string(),
    }),
    onSubmit: (values) => {
      setCurrentFilters(values);
    },
  });

  const noDataConfig = {
    noDataTitle: `Nenhum TCE encontrado para ${situationTitle}`,
    noDataDescription: 'Use os filtros acima para refinar sua pesquisa.',
  };

  useEffect(() => {
    if (groupsUser.length === 0) {
      requestGetAllGroupsUser();
    }
    if (coursesUser.length === 0) {
      requestGetAllCoursesUser();
    }
    if (organizationsUser.length === 0) {
      requestGetAllOrganizationsUser();
    }

    if (
      initialFilters.entity?.length > 0 ||
      initialFilters.course?.length > 0 ||
      initialFilters.group?.length > 0
    ) {
      requestGetInternshipLocations();
    }
  }, []);

  useEffect(() => {
    const hasFilters =
      validation.values.entity.length > 0 ||
      validation.values.course.length > 0 ||
      validation.values.group.length > 0;

    if (hasFilters) {
      requestGetInternshipLocations();
    }
  }, [
    validation.values.entity,
    validation.values.course,
    validation.values.group,
  ]);

  useEffect(() => {
    if (listingType === undefined || listingType === null) {
      navigate('/tce');
      return;
    }

    const params: TceManagementUseCaseInterface.Params = {
      listingType: listingType,
      page: paginationMetadata.selectedPage,
      length: paginationMetadata.rowsPerPage,
      query: paginationMetadata.searchString,
      orderColumn: paginationMetadata.orderColumn,
      orderDirection: paginationMetadata.sortDirection,
      groups:
        currentFilters.group?.map((g: { value: string }) => g.value) || [],
      organizationHashIds:
        currentFilters.entity?.map((e: { value: string }) => e.value) || [],
      courseHashIds:
        currentFilters.course?.map((c: { value: string }) => c.value) || [],
      internshipLocations:
        currentFilters.local?.map((l: { value: string }) => l.value) || [],
      signatureStatus: signatureStatus || [],
      cpf: removeCpfMask(currentFilters.studentCpf || ''),
      studentName: currentFilters.studentName || '',
    };
    requestGetTceManagement({ ...params });
  }, [
    navigate,
    listingType,
    currentFilters,
    signatureStatus,
    paginationMetadata,
    requestGetTceManagement,
  ]);

  return (
    <PageWrapper title={`TCE - ${situationTitle}`}>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h4 className="d-inline-block mb-0">{situationTitle}</h4>
        <div>
          <Button
            color="light"
            onClick={() =>
              navigate('/tce', { state: { filters: currentFilters } })
            }
            className="me-2"
          >
            <i className="ri-arrow-left-line"></i> Voltar
          </Button>
        </div>
      </div>

      <TceFilters
        validation={validation}
        groupsUser={groupsUser}
        coursesUser={coursesUser}
        organizationsUser={organizationsUser}
        loadingReportSelectCourse={loadingReportSelectCourse}
        isSearching={listLoadingStatus === STATUSPAGE.LOADING}
        loadingInternshipLocations={loadingInternshipLocations}
        loadingReportSelectOrganization={loadingReportSelectOrganization}
        internshipLocationsSelectOptions={internshipLocationsSelectOptions}
      />
      <Card>
        <CardBody>
          <DataTable
            canSearch
            pagination
            sortServer
            actions={[]}
            searchServer
            paginationServer
            columns={useTceManagementColumns(listingType)}
            onSort={handleSortRemote}
            onSearch={handleSearchRemote}
            onChangePage={handlePageChange}
            data={tceManagementData?.tces || []}
            noDataTitle={noDataConfig.noDataTitle}
            onChangeRowsPerPage={handleRowsPerPageChange}
            noDataDescription={noDataConfig.noDataDescription}
            loading={listLoadingStatus === STATUSPAGE.LOADING}
            paginationTotalRows={tceManagementData?.total || 0}
          />
        </CardBody>
      </Card>
    </PageWrapper>
  );
};
